import React, { useCallback, useMemo, useState } from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button } from '@picsio/ui';

import Tooltip from '../../Tooltip';
import localization from '../../../shared/strings';
import Checkbox from '../../../UIComponents/checkbox';
import Icon from '../../Icon';
import styles from '../ConnectChannelSection/styles.module.scss';

function VariantsSelectionDialog({
  productTitle,
  variants,
  strings,
  variantsOptional,
  returnAllSelectedAsUndefined,
  onConfirm,
  onCancel,
  root,
}) {
  const [variantChecks, setVariantChecks] = useState(
    () => variants.reduce(
      (acc, variant) => ({ ...acc, [variant._id]: !variantsOptional }),
      {},
    ),
  );
  const [isProductSelected, setIsProductSelected] = useState(variantsOptional);

  const selectedVariantIds = useMemo(
    () => Object.entries(variantChecks).filter(([, checked]) => checked).map(([id]) => id),
    [variantChecks],
  );

  const allChecked = selectedVariantIds.length === variants.length;
  const noneChecked = selectedVariantIds.length === 0;

  const productCheckboxValue = useMemo(() => {
    if (variantsOptional) {
      return isProductSelected;
    }

    if (allChecked) {
      return true;
    }
    if (noneChecked) {
      return false;
    }

    return 'indeterminate';
  }, [allChecked, isProductSelected, noneChecked, variantsOptional]);

  const isContinueDisabled = variantsOptional
    ? !isProductSelected && noneChecked
    : noneChecked;

  const continueTooltip = isContinueDisabled
    ? (strings.SELECT_TARGET_TOOLTIP || strings.SELECT_VARIANT_TOOLTIP)
    : null;

  const destroy = useCallback(() => {
    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const handleContinue = useCallback(() => {
    if (variantsOptional) {
      destroy();
      onConfirm({ variantIds: selectedVariantIds, isProductSelected });
      return;
    }

    const variantIds = returnAllSelectedAsUndefined && allChecked
      ? undefined
      : selectedVariantIds;

    destroy();
    onConfirm({ variantIds });
  }, [
    allChecked,
    destroy,
    isProductSelected,
    onConfirm,
    returnAllSelectedAsUndefined,
    selectedVariantIds,
    variantsOptional,
  ]);

  const handleVariantToggle = useCallback((variantId) => {
    setVariantChecks((prev) => ({ ...prev, [variantId]: !prev[variantId] }));
  }, []);

  const handleProductToggle = useCallback(() => {
    if (variantsOptional) {
      setIsProductSelected((prev) => !prev);
      return;
    }

    setVariantChecks(
      variants.reduce((acc, variant) => ({ ...acc, [variant._id]: !allChecked }), {}),
    );
  }, [allChecked, variants, variantsOptional]);

  return (
    <div className={`simpleDialog ${styles.connectChannelSectionModal} ${styles.connectChannelSectionDialogWithSubheader}`}>
      <div className="simpleDialogUnderlayer" />
      <div className="simpleDialogBox">
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{strings.TITLE}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogSubHeader">
          <p>{strings.SUBTITLE}</p>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <Checkbox
              className="w-100 m-b-8 c-d"
              value={productCheckboxValue}
              label={productTitle}
              onChange={handleProductToggle}
            />
            <div className={styles.connectChannelSectionVariants}>
              {variants.map((variant) => (
                <Checkbox
                  key={variant._id}
                  className="m-b-8"
                  value={variantChecks[variant._id]}
                  label={variant.title}
                  onChange={() => handleVariantToggle(variant._id)}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button color="secondary" size="md" variant="contained" onClick={handleCancel}>
            {strings.BTN_CANCEL}
          </Button>
          <Tooltip content={continueTooltip}>
            <Button
              color="primary"
              size="md"
              variant="contained"
              disabled={isContinueDisabled}
              onClick={handleContinue}
            >
              {strings.BTN_CONTINUE || strings.BTN_OK}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

VariantsSelectionDialog.propTypes = {
  productTitle: PropTypes.string.isRequired,
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  strings: PropTypes.shape({
    TITLE: PropTypes.string.isRequired,
    SUBTITLE: PropTypes.string.isRequired,
    BTN_CANCEL: PropTypes.string.isRequired,
    BTN_CONTINUE: PropTypes.string,
    BTN_OK: PropTypes.string,
    SELECT_VARIANT_TOOLTIP: PropTypes.string,
    SELECT_TARGET_TOOLTIP: PropTypes.string,
  }),
  variantsOptional: PropTypes.bool,
  returnAllSelectedAsUndefined: PropTypes.bool,
  variants: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  })).isRequired,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func.isRequired,
};

VariantsSelectionDialog.defaultProps = {
  strings: localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL,
  variantsOptional: false,
  returnAllSelectedAsUndefined: true,
  onCancel: undefined,
};

export function showVariantsSelectionDialog({
  productTitle,
  variants,
  strings = localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL,
  variantsOptional = false,
  returnAllSelectedAsUndefined = true,
  onConfirm,
  onCancel,
}) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <VariantsSelectionDialog
      productTitle={productTitle}
      variants={variants}
      strings={strings}
      variantsOptional={variantsOptional}
      returnAllSelectedAsUndefined={returnAllSelectedAsUndefined}
      root={root}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />,
  );
}

export default VariantsSelectionDialog;
